import { useAtom } from 'jotai';
import { useCallback } from 'react';

import type { SessionId } from '@/features/rpa/recorder/api/recorder-bridge';
import { mergePersistedRecordings } from '@/features/rpa/recorder/lib/merge-persisted-recordings';
import { recordingStatusFromUploadStage } from '@/features/rpa/recorder/lib/recording-status-from-upload-stage';
import { recordingsAtom } from '@/features/rpa/recorder/model/recordings-atom';
import type { RecordingEntry } from '@/features/rpa/recorder/model/types';
import type {
  RecorderAnalysisStatusChangedPayload,
  RecorderUploadProgressPayload,
  RecorderUploadStateChangedPayload,
} from '@/tauri/rpa-recorder/upload-types';

export function useRecordings() {
  const [recordings, setRecordings] = useAtom(recordingsAtom);

  const addUploading = useCallback(
    (name: string, sessionId: SessionId, description: string | null): SessionId => {
      setRecordings((prev) => {
        if (prev.some((r) => r.sessionId === sessionId)) return prev;
        return [
          ...prev,
          {
            sessionId,
            name,
            description,
            status: 'uploading',
            uploadStage: 'pending',
            createdAtMs: Date.now(),
            isNew: true,
          },
        ];
      });
      return sessionId;
    },
    [setRecordings]
  );

  const applyUploadState = useCallback(
    (payload: RecorderUploadStateChangedPayload) => {
      setRecordings((prev) =>
        prev.map((r) => {
          if (r.sessionId !== payload.sessionId) return r;
          const next: RecordingEntry = {
            ...r,
            status: recordingStatusFromUploadStage(payload.stage),
            uploadStage: payload.stage,
            errorCode: payload.stage === 'failed' ? payload.errorCode : undefined,
            failureKind: payload.stage === 'failed' ? payload.failureKind : undefined,
            recordingId: payload.recordingId ?? r.recordingId,
          };
          if (payload.stage === 'uploading') return next;
          return {
            ...next,
            uploadBytes: undefined,
            uploadTotalBytes: undefined,
            uploadPartsCompleted: undefined,
            uploadPartsTotal: undefined,
          };
        })
      );
    },
    [setRecordings]
  );

  const applyUploadProgress = useCallback(
    (payload: RecorderUploadProgressPayload) => {
      setRecordings((prev) =>
        prev.map((r) =>
          r.sessionId === payload.sessionId
            ? {
                ...r,
                uploadBytes: payload.bytesUploaded,
                uploadTotalBytes: payload.bytesTotal,
                uploadPartsCompleted: payload.partsCompleted,
                uploadPartsTotal: payload.partsTotal,
              }
            : r
        )
      );
    },
    [setRecordings]
  );

  const applyAnalysisStatus = useCallback(
    (payload: RecorderAnalysisStatusChangedPayload) => {
      setRecordings((prev) =>
        prev.map((r) => {
          if (r.sessionId !== payload.sessionId) return r;
          if (payload.status === 'ready') {
            return {
              ...r,
              status: 'ready',
              uploadStage: 'ready',
              errorCode: undefined,
              failureKind: undefined,
              recordingId: payload.recordingId,
            };
          }
          if (payload.status === 'failed') {
            return {
              ...r,
              status: 'failed',
              uploadStage: 'failed',
              errorCode: payload.errorCode ?? 'GENERATION_FAILED',
              failureKind: 'generation',
              recordingId: payload.recordingId,
            };
          }
          return {
            ...r,
            status: 'polling',
            uploadStage: 'polling',
            recordingId: payload.recordingId,
          };
        })
      );
    },
    [setRecordings]
  );

  const markSeen = useCallback(
    (sessionId: SessionId) => {
      setRecordings((prev) =>
        prev.map((r) => (r.sessionId === sessionId ? { ...r, isNew: false } : r))
      );
    },
    [setRecordings]
  );

  const removeRecording = useCallback(
    (sessionId: SessionId) => {
      setRecordings((prev) => prev.filter((r) => r.sessionId !== sessionId));
    },
    [setRecordings]
  );

  const mergePersisted = useCallback(
    (persisted: readonly RecordingEntry[]) => {
      setRecordings((prev) => mergePersistedRecordings(prev, persisted));
    },
    [setRecordings]
  );

  return {
    recordings,
    addUploading,
    applyUploadState,
    applyUploadProgress,
    applyAnalysisStatus,
    markSeen,
    removeRecording,
    mergePersisted,
  };
}
